'use client';

import { useLearningSource, type LearningItem } from '@akademi/site-builder-runtime';
import { useAsyncResource } from '../_shared/useAsyncResource';

/** Default course-plugin id for podcast courses. */
export const PODCAST_PLUGIN_ID = 'C8Podcast';

/** Where a podcast block's cards come from — the live catalog or the
 *  authored array. */
export type PodcastSource = 'catalog' | 'authored';

const NONE: LearningItem[] = [];

/**
 * Podcast courses from the LMS catalog — the one feed behind every podcast
 * block ({@code Podcasts}, {@code FullHeightPodcast}), so they always list
 * the same shows.
 *
 * <p>{@code pluginIds} is the backend's course-type discriminator, so one
 * filter — {@code ['C8Podcast']} — is the whole query. Returns an empty list
 * while disabled, while the fetch is in flight, on a host without a
 * {@code LearningSource} (admin preview) and when the catalog has none.
 */
export function usePodcastCourses({
  enabled,
  pluginId = PODCAST_PLUGIN_ID,
  limit,
  sortField,
}: {
  enabled: boolean;
  pluginId?: string;
  limit: number;
  sortField: string;
}): LearningItem[] {
  const learning = useLearningSource();
  const canFetch = enabled && Boolean(learning?.searchCourses);

  const state = useAsyncResource<LearningItem[]>(
    async (signal) => {
      if (!canFetch) return null;
      const res = await learning!.searchCourses!({
        maxResults: limit,
        sortField,
        filter: { pluginIds: [pluginId] },
        signal,
      });
      return res?.items ?? [];
    },
    [canFetch, learning, limit, sortField, pluginId],
  );

  return state.kind === 'loaded' ? state.data : NONE;
}
